import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, startOfWeek, getDay, Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface CalendarProps {
    currentMonth: Date;
    setCurrentMonth: React.Dispatch<React.SetStateAction<Date>>;
    selectedDate: Date | null;
    setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>;
    availableDays: string[];
    handleDateSelection: (date: Date, index: number) => void;
    times: { realTime: string; utcTime: string }[];
    selectedTime: { realTime: string; utcTime: string } | null;
    setSelectedTime: React.Dispatch<React.SetStateAction<{ realTime: string; utcTime: string } | null>>;
    locale: Locale;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isPast: boolean;
    isPreviewMode?: boolean;
}

const Calendar: React.FC<CalendarProps> = ({ 
    currentMonth, 
    setCurrentMonth, 
    selectedDate, 
    setSelectedDate, 
    availableDays, 
    handleDateSelection, 
    times, 
    selectedTime, 
    setSelectedTime, 
    locale,
    setIsDialogOpen,
    isPast,
    isPreviewMode = false,
}) => {
    const [t] = useTranslation('calendar');
    const [viewMode, setViewMode] = useState<'calendar' | 'timeSelection'>('calendar');
    const daysOfWeek = [t('monday_short'), t('tuesday_short'), t('wednesday_short'), t('thursday_short'), t('friday_short'), t('saturday_short'), t('sunday_short')];
    const daysInMonth = eachDayOfInterval({
        start: startOfMonth(currentMonth),
        end: endOfMonth(currentMonth),
    });

    const startDayOfWeek = getDay(startOfMonth(currentMonth));
    const emptyDays = Array.from({ length: startDayOfWeek === 0 ? 6 : startDayOfWeek - 1 }).map((_, index) => (
        <div key={index} className="w-10 h-10"></div>
    ));

    const handleDateClick = (day: Date, index: number) => {
        if (!isPast) { // Add this condition
            handleDateSelection(day, index);
            if (window.innerWidth < 1024) {
                setViewMode('timeSelection');
            }
        }
    };

    return (
        <div className="flex flex-col min-h-0 justify-between">
            <div>
                <div className="flex flex-col lg:flex-row items-start gap-6">
                    {/* Calendar Section */}
                    {viewMode === 'calendar' && (
                        <div className="w-full lg:w-1/2">
                            {/* Month Navigation */}
                            <div className="flex justify-between items-center mb-6">
                                <button
                                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                    className="btn btn-ghost btn-sm"
                                    disabled={isPast} 
                                >
                                    ←
                                </button>
                                <h3 className="text-brand-heading text-lg">
                                    {format(currentMonth, 'MMMM yyyy', { locale })}
                                </h3>
                                <button
                                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                    className="btn btn-ghost btn-sm"
                                    disabled={isPast} 
                                >
                                    →
                                </button>
                            </div>

                            {/* Days of Week Header */}
                            <div className="grid grid-cols-7 gap-2 mb-4">
                                {daysOfWeek.map((day) => (
                                    <div key={day} className="text-center text-brand-caption font-semibold py-2">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Calendar Grid */}
                            <div className="grid grid-cols-7 gap-2">
                                {emptyDays}
                                {daysInMonth.map((day: any) => {
                                    const formattedDay = format(day, 'yyyy-MM-dd');
                                    const availableDayIndex = availableDays.indexOf(formattedDay);
                                    const isSelected = selectedDate && format(selectedDate, 'yyyy-MM-dd') === formattedDay;
                                    const isAvailable = availableDays.includes(formattedDay);
                                    
                                    return (
                                        <button
                                            key={day}
                                            onClick={() => isAvailable && handleDateClick(day, availableDayIndex)}
                                            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                                                isSelected
                                                    ? 'bg-brand-primary text-white shadow-lg'
                                                    : isAvailable
                                                        ? 'border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white'
                                                        : 'bg-base-200 text-base-content/30 cursor-not-allowed'
                                            }`}
                                            disabled={!isAvailable || isPast}
                                        >
                                            {format(day, 'd')}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Time Selection Section */}
                    <div className={`w-full lg:w-1/2 ${viewMode === 'calendar' ? 'hidden lg:block' : ''}`}>
                        {viewMode === 'timeSelection' && (
                            <button
                                onClick={() => setViewMode('calendar')}
                                className="btn btn-ghost btn-sm mb-4 lg:hidden"
                                disabled={isPast} 
                            >
                                ← {t('back')}
                            </button>
                        )}
                        
                        <div className="bg-base-100 rounded-lg p-4">
                            <h3 className="text-brand-heading mb-4">
                                {selectedDate
                                    ? t('availability', { date: format(selectedDate, 'EEEE d MMMM', { locale }) })
                                    : t('select_date')}
                            </h3>

                            <div className="flex flex-col gap-3 overflow-y-auto max-h-64">
                                {times.length > 0 ? (
                                    times.map((time, index) => (
                                        <button
                                            key={time.realTime}
                                            onClick={() => setSelectedTime(time)}
                                            className={`btn ${
                                                selectedTime?.realTime === time.realTime
                                                    ? 'btn-primary'
                                                    : 'btn-outline btn-primary'
                                            }`}
                                            disabled={isPast}
                                        >
                                            {time.utcTime}
                                        </button>
                                    ))
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-brand-caption">
                                            {selectedDate ? t('no_times_available') : t('select_date_first')}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={() => {
                                        if (isPreviewMode) {
                                            // Show preview mode toast notification using DaisyUI toast
                                            const toast = document.createElement('div');
                                            toast.className = 'toast toast-top toast-center';
                                            toast.innerHTML = `
                                                <div class="alert bg-brand-gradient text-white">
                                                    <span>🔍 Esta es una vista previa. Los clientes no pueden agendar citas reales en este modo.</span>
                                                </div>
                                            `;
                                            document.body.appendChild(toast);
                                            setTimeout(() => {
                                                if (document.body.contains(toast)) {
                                                    document.body.removeChild(toast);
                                                }
                                            }, 4000);
                                            return;
                                        }
                                        
                                        if (selectedDate && selectedTime) {
                                            setIsDialogOpen(true);
                                        } else {
                                            // Show error toast
                                            const toast = document.createElement('div');
                                            toast.className = 'toast toast-top toast-center';
                                            toast.innerHTML = `
                                                <div class="alert alert-brand-error">
                                                    <span>${t('select_date_time_error')}</span>
                                                </div>
                                            `;
                                            document.body.appendChild(toast);
                                            setTimeout(() => {
                                                if (document.body.contains(toast)) {
                                                    document.body.removeChild(toast);
                                                }
                                            }, 3000);
                                        }
                                    }}
                                    className={`btn ${
                                        isPreviewMode 
                                            ? 'bg-brand-gradient text-white border-0 hover:opacity-90'
                                            : 'btn btn-primary rounded-full'
                                    } btn-lg`}
                                    disabled={isPast || (!selectedDate || !selectedTime)}
                                >
                                    {isPreviewMode ? '🔍 Vista Previa' : t('appointment_confirm')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="w-full text-center mt-10 lg:mt-20">
                <p className="text-brand-caption">
                    Powered by <span className="text-brand-primary font-semibold">T-BIT</span>
                </p>
            </div>
        </div>
    );
};

export default Calendar;
